<template>
  <hm-table :id="$attrs['id']"
            :hover="hover"
            :cols="cols"
            :columns="columns"
            :grid-template-columns="gridTemplateColumns"
            :min-width="minWidth"
            :key="sortKey"
            :sticky="true">
    <slot name="table-head" />
    <slot :current-page="currentPage" />
    <div v-if="pages > 1" :class="paginateColumns" class="hm-table__td hm-table__td--no-hover height-auto">
      <div class="pagination">
        <hm-button :class="[{'disabled': pageNumber == 1}]"
                   class="btn--no-padding"
                   cols="1"
                   icon-left="back-2c"
                   @click="pageNumber = 1" />
        <hm-button :class="[{'disabled': pageNumber == 1}]"
                   class="btn--no-padding"
                   cols="1"
                   icon-left="back-1c"
                   @click="pageNumber = Math.max(1, pageNumber - 1)" />
        <hm-button v-if="pageNumber > pages - 2 && pageNumber >= 5" cols="1" @click="pageNumber = pageNumber - 4" v-html="pageNumber - 4" />
        <hm-button v-if="pageNumber > pages - 1 && pageNumber >= 4" cols="1" @click="pageNumber = pageNumber - 3" v-html="pageNumber - 3" />
        <hm-button v-if="pageNumber >= 3" cols="1" @click="pageNumber = pageNumber - 2" v-html="pageNumber - 2" />
        <hm-button v-if="pageNumber >= 2" cols="1" @click="pageNumber = pageNumber - 1" v-html="pageNumber - 1" />
        <hm-button class="disabled" cols="1" @click="pageNumber = pageNumber" v-html="pageNumber" />
        <hm-button v-if="pageNumber + 1 <= pages" cols="1" @click="pageNumber = pageNumber + 1" v-html="pageNumber + 1" />
        <hm-button v-if="pageNumber + 2 <= pages" cols="1" @click="pageNumber = pageNumber + 2" v-html="pageNumber + 2" />
        <hm-button v-if="pageNumber <= 2 && pageNumber + 3 <= pages" cols="1" @click="pageNumber = pageNumber + 3" v-html="pageNumber + 3" />
        <hm-button v-if="pageNumber == 1 && pageNumber + 4 <= pages" cols="1" @click="pageNumber = pageNumber + 4" v-html="pageNumber + 4" />
        <hm-button :class="[{'disabled': pageNumber === pages}]"
                   class="btn--no-padding"
                   cols="1"
                   icon-left="forward-1c"
                   @click="pageNumber = Math.min(pages, pageNumber + 1)" />
        <hm-button :class="[{'disabled': pageNumber === pages}]"
                   class="btn--no-padding"
                   cols="1"
                   icon-left="forward-2c"
                   @click="pageNumber = pages" />
      </div>
    </div>
  </hm-table>
</template>

<script>

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { split, endsWith, head, isNil, isEmpty, slice, isString, cloneDeep, toNumber, get } from 'lodash';
import uuid from 'uuid';

export default {
  name: 'PaginatedTable',

  mixins: [HotelmeisterMixin],

  props: {
    sticky: {
      required: false,
      type: Boolean,
      default: false,
    },
    columns: {
      required: true,
      type: String,
      default: '1',
    },
    gridTemplateColumns: {
      required: false,
      type: String,
      default: null,
    },
    minWidth: {
      required: false,
      type: String,
      default: null,
    },
    hover: {
      required: false,
      type: Boolean,
      default: true,
    },
    cols: {
      type: String,
      required: false,
      default: '12',
    },
    rows: {
      type: Array,
      required: true,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      required: false,
      default: 10,
    },
  },

  data: function () {
    return {
      pageNumber: 1,
      sortedRows: this.rows,
      sort: null,
      sortDirection: 'asc',
      sortKey: uuid.v4(),
    };
  },

  computed: {
    pages: function () {
      return Math.ceil(this.sortedRows.length / this.itemsPerPage);
    },
    currentPage: function () {
      const offset = (this.pageNumber - 1) * this.itemsPerPage;
      return slice(this.sortedRows, offset, offset + this.itemsPerPage);
    },
    paginateColumns: function () {
      return this.columns.split(' ').map(it => `col-${it}`).join(' ');
    },
  },

  watch: {
    rows: function () {
      this.sortedRows = cloneDeep(this.rows);
      this.sort = null;
      this.sortDirection = 'asc';
      this.sortKey = uuid.v4();

      if (this.pageNumber > Math.ceil(this.sortedRows.length / this.itemsPerPage)) {
        this.pageNumber = 1;
      }
    },
  },

  created: function () {
    this.$on('sort', this.sortBy);
  },

  methods: {
    sortBy(fieldName, isNumber = false) {
      if (this.sort === fieldName) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortDirection = 'asc';
      }

      this.sort = fieldName;

      this.sortedRows.sort((a, b) => {
        if (isNil(a) && !isNil(b)) return 1;
        if (!isNil(a) && isNil(b)) return -1;
        if (isNil(a) && isNil(b)) return 0;

        const valA = get(a, fieldName);
        const valB = get(b, fieldName);

        if (isNil(valA) && !isNil(valB)) return 1;
        if (!isNil(valA) && isNil(valB)) return -1;
        if (isNil(valA) && isNil(valB)) return 0;

        if (isNumber === true) {
          return toNumber(valA) - toNumber(valB);
        } else if (isString(valA) && isString(valB)) {
          // eslint-disable-next-line no-nested-ternary
          return valA.toUpperCase() > valB.toUpperCase() ? 1 : (valA.toUpperCase() < valB.toUpperCase() ? -1 : 0);
        } else {
          // eslint-disable-next-line no-nested-ternary
          return valA > valB ? 1 : (valA < valB ? -1 : 0);
        }
      });

      if (this.sortDirection === 'desc') {
        this.sortedRows.reverse();
      }

      this.sortKey = uuid.v4();
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  @import "~styles/import";

  .quick-action-button {
    padding: 0!important;
  }

  .btn--no-padding {
    padding-right: 0!important;
  }

  .pagination {
    display: grid;
    grid-auto-columns: 3.5rem;
    grid-template-rows: auto;
    grid-column-gap: 1rem;
    float: right;

    .btn--inline {
      justify-content: center;
      &.disabled {
        color: $middlegray;
        cursor: not-allowed;
        &:hover {
          background: inherit;
          border: 1px solid $middlegray;
        }
      }
    }

    * {
      grid-row: 1;
    }
  }
</style>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/lib/table/PaginatedTable.vue